// 레이드 VFX 입자 풀(raidVfx.ts가 쓴다). CPU 풀 → InstancedMesh 한 개(드로우콜 1). 미리 할당한 typed array만 쓰고 프레임 루프에서 새 객체를 만들지 않는다.
// 가산 풀(불씨·불똥 줄·불꽃 덩이·광휘·위협 글리프·고리·빛기둥)과 알파 풀(흙먼지·그을음 연기)을 같은 클래스로 만든다(mode).
// 방향: 0 카메라 정면, 1 속도 방향으로 늘인 줄(불똥·화살), 2 세운 판(세로축 고정 — 빛기둥·열기).
// 색은 인스턴스 속성 aCol(HDR, 1 넘어도 됨)로 넣고 셰이더가 모양·흰 심·수명 색 변화를 정한다.
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';

type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any
const { abs, atan, attribute, cos, float, fract, length, max, min, mix, sin, smoothstep, uniform, uv, vec2, vec3 } = TSL as unknown as Record<string, N>;

/** 가산 풀 모양 */
export const AddKind = { Ember: 0, Streak: 1, Flame: 2, Flare: 3, Glyph: 4, Ring: 5, Pillar: 6, Heat: 7, Rune: 8 } as const;
/** 알파 풀 모양 */
export const AlphaKind = { Dust: 0, Smoke: 1 } as const;
/** 방향 */
export const Orient = { Camera: 0, Velocity: 1, Upright: 2 } as const;

/** 품질 배율 1 기준 상한(테스트·문서가 같은 값을 읽는다) */
export const PARTICLE_CAP = { add: 720, alpha: 200 } as const;

export class RaidParticles {
  readonly mesh: THREE.InstancedMesh;
  readonly cap: number;
  /** 품질 배율(0.25..1): 생성 수를 줄인다(상한은 생성 때 정함) */
  scale = 1;
  /** 섬광 줄이기: 가산 밝기 배율 */
  dim = 1;
  private n = 0;
  private readonly px: Float32Array; private readonly py: Float32Array; private readonly pz: Float32Array;
  private readonly vx: Float32Array; private readonly vy: Float32Array; private readonly vz: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array;
  private readonly s0: Float32Array; private readonly s1: Float32Array;
  private readonly cr: Float32Array; private readonly cg: Float32Array; private readonly cb: Float32Array;
  private readonly a0: Float32Array; private readonly grav: Float32Array; private readonly drag: Float32Array;
  private readonly kind: Uint8Array; private readonly orient: Uint8Array; private readonly stretch: Float32Array;
  private readonly seed: Float32Array; private readonly aspect: Float32Array;
  private readonly aP: THREE.InstancedBufferAttribute;
  private readonly aCol: THREE.InstancedBufferAttribute;
  private readonly time = uniform(0);
  private rs = 0x2f6b1d3;
  /** QA: 누적 생성·상한 때문에 버린 수 */
  spawned = 0;
  dropped = 0;
  private readonly R = new THREE.Vector3();
  private readonly U = new THREE.Vector3();
  private readonly F = new THREE.Vector3();

  constructor(readonly mode: 'add' | 'alpha', particleScale: number, name: string) {
    const base = mode === 'add' ? PARTICLE_CAP.add : PARTICLE_CAP.alpha;
    this.cap = Math.max(32, Math.floor(base * Math.max(0.25, Math.min(1, particleScale))));
    this.scale = Math.max(0.25, Math.min(1, particleScale));
    const geo = new THREE.PlaneGeometry(1, 1);
    this.aP = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 4), 4);
    this.aP.setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 3), 3);
    this.aCol.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aP', this.aP);
    geo.setAttribute('aCol', this.aCol);
    const mat = mode === 'add' ? createAddMaterial(this.time) : alphaMaterial(this.time);
    this.mesh = new THREE.InstancedMesh(geo, mat, this.cap);
    this.mesh.name = name;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.renderOrder = mode === 'add' ? 23 : 21;
    const f = (): Float32Array => new Float32Array(this.cap);
    this.px = f(); this.py = f(); this.pz = f(); this.vx = f(); this.vy = f(); this.vz = f();
    this.age = f(); this.life = f(); this.s0 = f(); this.s1 = f();
    this.cr = f(); this.cg = f(); this.cb = f(); this.a0 = f(); this.grav = f(); this.drag = f();
    this.kind = new Uint8Array(this.cap); this.orient = new Uint8Array(this.cap); this.stretch = f();
    this.seed = f(); this.aspect = f();
  }

  get active(): number { return this.n; }
  get free(): number { return this.cap - this.n; }

  /** 0..1 결정적 난수 */
  rnd(): number {
    this.rs = (Math.imul(this.rs, 1664525) + 1013904223) >>> 0;
    return this.rs / 4294967296;
  }
  /** a..b 난수 */
  range(a: number, b: number): number { return a + (b - a) * this.rnd(); }
  /** 품질 배율을 적용한 개수(최소 min) */
  count(n: number, minimum = 1): number { return Math.max(minimum, Math.round(n * this.scale)); }

  /**
   * 입자 하나. color = 0xRRGGBB, bright = HDR 배율(1 넘으면 블룸), size0→size1(m) 수명 동안 바뀐다.
   * grav: 위로 +(m/s², 아래로 떨어지면 음수), drag: 초당 감속 비율, stretch: 속도 방향 줄 길이 배율(Orient.Velocity), aspect: 세로/가로(Upright·Camera)
   */
  emit(kind: number, orient: number, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number,
    size0: number, size1: number, color: number, bright: number, alpha: number, grav = 0, drag = 0, stretch = 0, aspect = 1): number {
    if (this.n >= this.cap) { this.dropped++; return -1; }
    const i = this.n++;
    this.kind[i] = kind; this.orient[i] = orient;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z; this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz;
    this.age[i] = 0; this.life[i] = Math.max(0.02, life); this.s0[i] = size0; this.s1[i] = size1;
    this.cr[i] = (color >> 16 & 255) / 255 * bright; this.cg[i] = (color >> 8 & 255) / 255 * bright; this.cb[i] = (color & 255) / 255 * bright;
    this.a0[i] = alpha; this.grav[i] = grav; this.drag[i] = drag; this.stretch[i] = stretch; this.aspect[i] = aspect;
    this.seed[i] = this.rnd();
    this.spawned++;
    return i;
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.kind[i] = this.kind[j]; this.orient[i] = this.orient[j];
    this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.pz[i] = this.pz[j];
    this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.s0[i] = this.s0[j]; this.s1[i] = this.s1[j];
    this.cr[i] = this.cr[j]; this.cg[i] = this.cg[j]; this.cb[i] = this.cb[j]; this.a0[i] = this.a0[j];
    this.grav[i] = this.grav[j]; this.drag[i] = this.drag[j]; this.stretch[i] = this.stretch[j]; this.aspect[i] = this.aspect[j];
    this.seed[i] = this.seed[j];
  }

  /** 시뮬레이션만(테스트·렌더 없는 갱신). dt = 초 */
  simulate(dt: number): void {
    if (dt <= 0) return;
    let i = 0;
    while (i < this.n) {
      this.age[i] += dt;
      if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
      const k = Math.max(0, 1 - this.drag[i] * dt);
      this.vx[i] *= k; this.vy[i] = this.vy[i] * k + this.grav[i] * dt; this.vz[i] *= k;
      this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt;
      // 바닥 아래로 내려간 불똥은 바닥에서 튄다(한 번 약하게)
      if (this.py[i] < 0.03 && this.vy[i] < 0) { this.py[i] = 0.03; this.vy[i] *= -0.25; this.vx[i] *= 0.6; this.vz[i] *= 0.6; }
      i++;
    }
  }

  /** 매 프레임: 시뮬레이션 + 행렬·속성 쓰기. dt = 세계 시계 초(멈추면 0), time = 셰이더 시간(초) */
  update(dt: number, camera: THREE.Camera, time: number): void {
    this.simulate(dt);
    this.time.value = time;
    camera.matrixWorld.extractBasis(this.R, this.U, this.F);
    const R = this.R, U = this.U, F = this.F;
    // 세운 판: 카메라 오른쪽을 수평으로 눕힌 축
    let hx = R.x, hz = R.z;
    const hl = Math.hypot(hx, hz) || 1; hx /= hl; hz /= hl;
    const m = this.mesh.instanceMatrix.array as Float32Array, p = this.aP.array as Float32Array, c = this.aCol.array as Float32Array;
    const dimAdd = this.mode === 'add' ? this.dim : 1;
    for (let i = 0; i < this.n; i++) {
      const t = this.age[i] / this.life[i];
      const w = this.s0[i] + (this.s1[i] - this.s0[i]) * t;
      const o = i * 16;
      let ax = R.x * w, ay = R.y * w, az = R.z * w, bx = U.x * w * this.aspect[i], by = U.y * w * this.aspect[i], bz = U.z * w * this.aspect[i];
      const or = this.orient[i];
      if (or === Orient.Velocity) {
        // 속도를 화면 평면에 눕힌 방향으로 늘인다(길이 = 속도 × stretch, 최소 w)
        const vx = this.vx[i], vy = this.vy[i], vz = this.vz[i];
        const d = vx * F.x + vy * F.y + vz * F.z;
        let sx = vx - F.x * d, sy = vy - F.y * d, sz = vz - F.z * d;
        const sl = Math.hypot(sx, sy, sz);
        if (sl > 1e-4) {
          sx /= sl; sy /= sl; sz /= sl;
          const len = Math.max(w, Math.hypot(vx, vy, vz) * this.stretch[i]);
          ax = sx * len; ay = sy * len; az = sz * len;
          // 폭 축 = F × s
          bx = (F.y * sz - F.z * sy) * w; by = (F.z * sx - F.x * sz) * w; bz = (F.x * sy - F.y * sx) * w;
        }
      } else if (or === Orient.Upright) {
        ax = hx * w; ay = 0; az = hz * w;
        bx = 0; by = w * this.aspect[i]; bz = 0;
      }
      m[o] = ax; m[o + 1] = ay; m[o + 2] = az; m[o + 3] = 0;
      m[o + 4] = bx; m[o + 5] = by; m[o + 6] = bz; m[o + 7] = 0;
      m[o + 8] = F.x; m[o + 9] = F.y; m[o + 10] = F.z; m[o + 11] = 0;
      m[o + 12] = this.px[i]; m[o + 13] = this.py[i]; m[o + 14] = this.pz[i]; m[o + 15] = 1;
      // 들어올 때 빠르게(수명 앞 8%), 나갈 때 부드럽게
      const fade = Math.min(1, t * 12) * (1 - t * t);
      p[i * 4] = this.a0[i] * fade; p[i * 4 + 1] = this.kind[i]; p[i * 4 + 2] = t; p[i * 4 + 3] = this.seed[i];
      c[i * 3] = this.cr[i] * dimAdd; c[i * 3 + 1] = this.cg[i] * dimAdd; c[i * 3 + 2] = this.cb[i] * dimAdd;
    }
    this.mesh.count = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n > 0) { this.mesh.instanceMatrix.needsUpdate = true; this.aP.needsUpdate = true; this.aCol.needsUpdate = true; }
  }

  /** 셰이더 미리 컴파일 */
  prewarm(on: boolean): void { this.mesh.count = on ? 1 : this.n; this.mesh.visible = on || this.n > 0; }

  clear(): void { this.n = 0; this.mesh.count = 0; this.mesh.visible = false; }

  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

/**
 * 고정 칸 표식(드로우콜 1): 따라다니는 것(도발 방패 빛·보스 위 위협 글리프·치유 빛기둥·사제 시전 빛·광폭 열기)을 칸 번호로 매 프레임 놓는다.
 * 입자 풀과 달리 칸이 움직이지 않으므로(교환 삭제 없음) 소유자가 칸 번호를 들고 쓴다. 쓰지 않는 칸은 알파 0.
 */
export class RaidMarkers {
  readonly mesh: THREE.InstancedMesh;
  readonly cap: number;
  dim = 1;
  private readonly aP: THREE.InstancedBufferAttribute;
  private readonly aCol: THREE.InstancedBufferAttribute;
  private readonly time = uniform(0);
  private readonly used: Uint8Array;
  private readonly R = new THREE.Vector3();
  private readonly U = new THREE.Vector3();
  private readonly F = new THREE.Vector3();

  constructor(cap: number) {
    this.cap = cap;
    const geo = new THREE.PlaneGeometry(1, 1);
    this.aP = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    this.aP.setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    this.aCol.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aP', this.aP);
    geo.setAttribute('aCol', this.aCol);
    this.mesh = new THREE.InstancedMesh(geo, createAddMaterial(this.time), cap);
    this.mesh.name = 'fx-raid-markers';
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.renderOrder = 24;
    this.used = new Uint8Array(cap);
  }

  /** 프레임 시작: 모든 칸을 비운다(이번 프레임에 set한 칸만 보인다) */
  begin(camera: THREE.Camera, time: number): void {
    this.used.fill(0);
    this.time.value = time;
    camera.matrixWorld.extractBasis(this.R, this.U, this.F);
  }

  /** 칸 i에 표식. t = 셰이더 수명 비율(불꽃 식음 등), orient = Camera·Upright */
  set(i: number, kind: number, orient: number, x: number, y: number, z: number, size: number, aspect: number, color: number, bright: number, alpha: number, t = 0, seed = 0.5): void {
    if (i < 0 || i >= this.cap || alpha <= 0.002) return;
    this.used[i] = 1;
    const R = this.R, U = this.U, F = this.F, m = this.mesh.instanceMatrix.array as Float32Array, o = i * 16;
    if (orient === Orient.Upright) {
      let hx = R.x, hz = R.z; const hl = Math.hypot(hx, hz) || 1; hx /= hl; hz /= hl;
      m[o] = hx * size; m[o + 1] = 0; m[o + 2] = hz * size; m[o + 4] = 0; m[o + 5] = size * aspect; m[o + 6] = 0;
    } else {
      m[o] = R.x * size; m[o + 1] = R.y * size; m[o + 2] = R.z * size; m[o + 4] = U.x * size * aspect; m[o + 5] = U.y * size * aspect; m[o + 6] = U.z * size * aspect;
    }
    m[o + 3] = 0; m[o + 7] = 0;
    m[o + 8] = F.x; m[o + 9] = F.y; m[o + 10] = F.z; m[o + 11] = 0;
    m[o + 12] = x; m[o + 13] = y; m[o + 14] = z; m[o + 15] = 1;
    const p = this.aP.array as Float32Array, c = this.aCol.array as Float32Array;
    p[i * 4] = alpha; p[i * 4 + 1] = kind; p[i * 4 + 2] = t; p[i * 4 + 3] = seed;
    const k = bright * this.dim;
    c[i * 3] = (color >> 16 & 255) / 255 * k; c[i * 3 + 1] = (color >> 8 & 255) / 255 * k; c[i * 3 + 2] = (color & 255) / 255 * k;
  }

  /** 프레임 끝: 안 쓴 칸은 알파 0, 그린 칸 수를 정한다 */
  end(): number {
    const p = this.aP.array as Float32Array;
    let last = -1;
    for (let i = 0; i < this.cap; i++) { if (this.used[i]) last = i; else p[i * 4] = 0; }
    this.mesh.count = last + 1;
    this.mesh.visible = last >= 0;
    if (last >= 0) { this.mesh.instanceMatrix.needsUpdate = true; this.aP.needsUpdate = true; this.aCol.needsUpdate = true; }
    return last + 1;
  }

  prewarm(on: boolean): void { this.mesh.count = on ? 1 : 0; this.mesh.visible = on; }
  clear(): void { this.used.fill(0); this.mesh.count = 0; this.mesh.visible = false; }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

/** 값이 k와 같은 칸이면 1(정수 종류 고르기) */
const is = (v: N, k: number): N => float(1).sub(smoothstep(float(0.25), float(0.5), abs(v.sub(k))));

/** 값 잡음(사인 합, 텍스처 없음) */
function wobble(p: N, s: N, t: N): N {
  return sin(p.x.mul(4.7).add(s.mul(17)).add(t.mul(5.3))).mul(sin(p.y.mul(3.9).sub(t.mul(6.1)).add(s.mul(9))))
    .add(sin(p.x.mul(9.1).sub(p.y.mul(7.3)).add(t.mul(9)).add(s.mul(31))).mul(0.5));
}

/** 가산 입자 재질(RaidMarkers도 같이 쓴다). 속성 aP(알파·종류·수명 비율·씨앗)·aCol(HDR 색) */
export function createAddMaterial(time: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
  m.forceSinglePass = true; m.fog = false;
  const P: N = attribute('aP', 'vec4');
  const col: N = attribute('aCol', 'vec3');
  const alpha = P.x, kind = P.y, t = P.z, seed = P.w;
  const q: N = uv().sub(0.5).mul(2);
  const d: N = length(q);
  // 불씨: 위로 긴 물방울
  const emberD = length(vec2(q.x.mul(1.35), q.y.mul(0.8)));
  const ember = smoothstep(float(1), float(0), emberD).pow(1.7);
  // 불똥 줄: x = 길이(+x가 머리), y = 폭. 꼬리로 갈수록 가늘고 옅다
  const along = q.x.mul(0.5).add(0.5);
  const thick = float(1).sub(smoothstep(along.mul(0.75).add(0.08), along.mul(0.75).add(0.28), abs(q.y)));
  const streak = thick.mul(smoothstep(float(0), float(0.35), along)).mul(float(1).sub(smoothstep(float(0.92), float(1), along)));
  // 불꽃 덩이: 가장자리를 흔든 원. 늙을수록 성기고 커진다
  const wob = wobble(q, seed, time.add(seed.mul(3)));
  const flameD = d.add(wob.mul(0.16).mul(float(0.5).add(t)));
  const flame = smoothstep(float(1), float(0.25), flameD).mul(float(1).sub(t.mul(0.35)));
  // 광휘: 부드러운 원 + 뜨거운 심
  const flare = smoothstep(float(1), float(0), d).pow(2.6).add(smoothstep(float(0.35), float(0), d).mul(0.8));
  // 위협 글리프: 아래를 가리키는 갈매기 두 개(위아래로 조금 떨어져)
  const chev = (oy: number): N => {
    const v = q.y.sub(oy).add(abs(q.x).mul(0.85));
    return float(1).sub(smoothstep(float(0.09), float(0.17), abs(v))).mul(float(1).sub(smoothstep(float(0.62), float(0.72), abs(q.x))));
  };
  const glyph = max(chev(0.32), chev(-0.12)).add(smoothstep(float(1), float(0.2), d).mul(0.18));
  // 고리
  const ring = float(1).sub(smoothstep(float(0.05), float(0.16), abs(d.sub(0.8)))).add(smoothstep(float(0.8), float(0), d).mul(0.12));
  // 빛기둥: 가로로 가운데가 밝고 위로 갈수록 옅다. 위로 흐르는 결
  const ax = abs(q.x);
  const flow = fract(q.y.mul(1.6).sub(time.mul(1.4)).add(seed.mul(7)).add(sin(q.x.mul(9).add(seed.mul(13))).mul(0.08)));
  const pillar = smoothstep(float(1), float(0), ax).pow(2.2).mul(smoothstep(float(-1), float(-0.75), q.y)).mul(smoothstep(float(1), float(0.1), q.y))
    .mul(float(0.65).add(smoothstep(float(0.7), float(1), flow).mul(0.6)));
  // 열기: 세로로 일렁이는 옅은 띠(굴절 대신 아주 옅은 가산 물결)
  const heatW = sin(q.y.mul(6).sub(time.mul(5)).add(seed.mul(20))).mul(0.25);
  const heat = smoothstep(float(0.9), float(0), abs(q.x.add(heatW))).mul(smoothstep(float(1), float(0.3), abs(q.y))).mul(0.5);
  // 잿불 룬(하드 낙인, raidVfxBrand): t = 회전 위상(0..1). 여섯 칸으로 끊긴 바깥 고리 + 칸 경계의 안쪽 눈금 + 도는 마름모 + 옅은 속빛
  const rot = t.mul(6.2832);
  const rc = cos(rot), rs = sin(rot);
  const rx = q.x.mul(rc).sub(q.y.mul(rs)), ry = q.x.mul(rs).add(q.y.mul(rc));
  const seg = fract(atan(ry, rx).mul(6 / 6.2832));
  const segEdge = min(seg, float(1).sub(seg));
  const runeOuter = float(1).sub(smoothstep(float(0.05), float(0.11), abs(d.sub(0.8)))).mul(smoothstep(float(0.05), float(0.12), segEdge));
  const runeTick = float(1).sub(smoothstep(float(0.025), float(0.06), segEdge)).mul(smoothstep(float(0.44), float(0.5), d)).mul(float(1).sub(smoothstep(float(0.62), float(0.68), d)));
  const runeCore = float(1).sub(smoothstep(float(0.05), float(0.1), abs(abs(rx).add(abs(ry)).sub(0.36))));
  const rune = max(max(runeOuter, runeTick), runeCore).add(smoothstep(float(0.9), float(0), d).mul(0.14));
  const shape = ember.mul(is(kind, 0)).add(streak.mul(is(kind, 1))).add(flame.mul(is(kind, 2))).add(flare.mul(is(kind, 3)))
    .add(glyph.mul(is(kind, 4))).add(ring.mul(is(kind, 5))).add(pillar.mul(is(kind, 6))).add(heat.mul(is(kind, 7))).add(rune.mul(is(kind, 8)));
  // 흰 심: 불씨·광휘·불꽃의 가운데, 불똥 줄의 머리. 불꽃은 늙을수록 붉게 식는다
  const core = smoothstep(float(0.45), float(0), d).mul(is(kind, 0).add(is(kind, 3)))
    .add(smoothstep(float(0.6), float(0.05), flameD).mul(float(1).sub(t).pow(2)).mul(is(kind, 2)))
    .add(thick.mul(smoothstep(float(0.55), float(0.95), along)).mul(is(kind, 1)));
  const cool = mix(float(1), float(0.55), t.mul(is(kind, 2)));
  // 흰 심은 좁고 따뜻하게(넓게 희면 블룸에서 색이 날아간다)
  m.colorNode = mix(col.mul(cool), vec3(1.0, 0.82, 0.55).mul(max(col.x, max(col.y, col.z))), min(core, 1).mul(0.4));
  m.opacityNode = shape.mul(alpha).clamp(0, 1);
  return m;
}

function alphaMaterial(time: N): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
  m.forceSinglePass = true;
  const P: N = attribute('aP', 'vec4');
  const col: N = attribute('aCol', 'vec3');
  const alpha = P.x, kind = P.y, t = P.z, seed = P.w;
  const q: N = uv().sub(0.5).mul(2);
  const wob = wobble(q.mul(0.8), seed, time.mul(0.35).add(seed));
  const dd = length(q).add(wob.mul(0.14).mul(float(0.6).add(t)));
  // 흙먼지: 가장자리가 무른 덩이, 위쪽이 밝다(빛을 받는 쪽)
  const puff = smoothstep(float(1), float(0.15), dd).pow(1.3);
  const lit = q.y.mul(0.5).add(0.5);
  const dust = mix(col.mul(0.72), col.mul(1.12), lit);
  // 그을음 연기: 더 어둡고 더 무르다
  const smoke = mix(col.mul(0.55), col.mul(0.9), lit);
  m.colorNode = dust.mul(is(kind, 0)).add(smoke.mul(is(kind, 1)));
  m.opacityNode = puff.mul(alpha).mul(mix(float(1), float(0.8), is(kind, 1))).clamp(0, 1);
  return m;
}
